/* eslint-disable no-unused-expressions */

import React, { Component } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { withTranslation } from "react-i18next";
import sysJwt from "../../../utils/sysJwt";
import { post } from "../../../utils/sysAxios";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { confirmAlert } from "react-confirm-alert";
import SysTable from "../../commonComponents/SysTable";
import { toastError } from "../../../utils/sysToast";

/**
 * Bulk issue of service bills, maker-checker.
 * Maker (CB96) uploads an Excel file, checker (CB97) approves or rejects it.
 * Bills are only created at approval time.
 */
class BulkBills extends Component {
  state = {
    tableData: [],
    allServices: [],
    compoundsDropdown: [],

    filterStatus: "",
    filterService: "",

    statusOptions: [
      { label: "Pending", value: "Pending" },
      { label: "Approved", value: "Approved" },
      { label: "Rejected", value: "Rejected" },
    ],

    // Upload dialog (maker)
    inUploadMode: false,
    uploadStage: "setup", // "setup" | "preview"
    uploadServiceId: "",
    uploadCompoundId: "",
    uploadFile: null,
    previewRows: [],
    previewErrorCount: 0,
    previewTotalAmount: 0,

    // Rows dialog (viewer / checker)
    inRowsMode: false,
    selectedBatch: null,
    batchRows: [],

    // Reject dialog (checker)
    inRejectMode: false,
    rejectBatch: null,
    rejectNotes: "",
  };

  componentDidMount() {
    this.getData();
    this.getAllServices();
    this.getCompoundsDropdown();
  }

  getAllServices() {
    post("/api/data/GetAllServices", null, this.props.updateIsLoading).then(
      (apiResponse) => {
        this.setState({ allServices: apiResponse.data.respData.data });
      }
    );
  }

  getCompoundsDropdown() {
    post(
      "/api/data/GetAllCompoundsDropdown",
      null,
      this.props.updateIsLoading
    ).then((apiResponse) => {
      this.setState({ compoundsDropdown: apiResponse.data.respData.data });
    });
  }

  getData() {
    post(
      "/api/data/GetBulkBillBatches",
      {
        status: this.state.filterStatus || "",
        serviceId: this.state.filterService || 0,
      },
      this.props.updateIsLoading
    ).then((apiResponse) => {
      this.setState({ tableData: apiResponse.data.respData.data });
    });
  }

  // ── Maker ──────────────────────────────────────────────────────────

  openUpload() {
    this.setState({
      inUploadMode: true,
      uploadStage: "setup",
      uploadServiceId: "",
      uploadCompoundId: "",
      uploadFile: null,
      previewRows: [],
      previewErrorCount: 0,
      previewTotalAmount: 0,
    });
  }

  closeUpload() {
    this.setState({ inUploadMode: false });
    this.getData();
  }

  downloadTemplate() {
    this.props.updateIsLoading(true);
    axios
      .post(
        "/api/data/DownloadBulkBillsTemplate",
        { compoundId: this.state.uploadCompoundId || 0 },
        {
          headers: { Authorization: "Bearer " + sessionStorage.getItem("748784") },
          responseType: "blob",
        }
      )
      .then((res) => {
        saveAs(res.data, "BulkBillsTemplate.xlsx");
      })
      .finally(() => {
        this.props.updateIsLoading(false);
      });
  }

  doPreview() {
    const { uploadFile, uploadServiceId } = this.state;
    if (!uploadFile) return;

    if (!uploadServiceId) {
      toastError(this.props.t("bulkBills.serviceRequired"));
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile, uploadFile.name);
    formData.append("serviceId", uploadServiceId);

    post("/api/data/PreviewBulkBills", formData, this.props.updateIsLoading).then(
      (apiResponse) => {
        if (apiResponse && apiResponse.data.respCode === 0) {
          this.setState({
            previewRows: apiResponse.data.respData.rows,
            previewErrorCount: apiResponse.data.respData.errorCount,
            previewTotalAmount: apiResponse.data.respData.totalAmount,
            uploadStage: "preview",
          });
        }
      }
    );
  }

  doSubmit() {
    const { uploadFile, uploadServiceId } = this.state;

    const formData = new FormData();
    formData.append("file", uploadFile, uploadFile.name);
    formData.append("serviceId", uploadServiceId);

    post(
      "/api/data/SubmitBulkBillBatch",
      formData,
      this.props.updateIsLoading
    ).then((apiResponse) => {
      if (apiResponse && apiResponse.data.respCode === 0) {
        this.closeUpload();
      }
    });
  }

  // ── Viewer / checker ───────────────────────────────────────────────

  openRows(batch) {
    post(
      "/api/data/GetBulkBillBatchRows",
      { batchId: batch.ID },
      this.props.updateIsLoading
    ).then((apiResponse) => {
      this.setState({
        inRowsMode: true,
        selectedBatch: batch,
        batchRows: apiResponse.data.respData.data,
      });
    });
  }

  closeRows() {
    this.setState({ inRowsMode: false, selectedBatch: null, batchRows: [] });
    this.getData();
  }

  handleApprove(batch) {
    const { t } = this.props;
    confirmAlert({
      title: t("bulkBills.approveTitle"),
      message: `${t("bulkBills.approveConfirm")} (${batch.ROWS_TOTAL} ${t(
        "bulkBills.rows"
      )}, ${t("col.amount")}: ${(batch.TOTAL_AMOUNT || 0).toLocaleString()})`,
      buttons: [
        {
          label: t("btn.yes"),
          onClick: () => {
            post(
              "/api/data/ApproveBulkBillBatch",
              { batchId: batch.ID, notes: "" },
              this.props.updateIsLoading
            ).then((apiResponse) => {
              if (apiResponse && apiResponse.data.respCode === 0) {
                // Reopen with the batch as it now stands. Reusing the local copy
                // would keep it "Pending", leaving Approve/Reject on screen with
                // stale checker details.
                const applied = apiResponse.data.respData;
                this.getData();
                this.openRows({
                  ...batch,
                  STATUS: applied.status,
                  CHECKED_BY_NAME: applied.checkedByName,
                  CHECKED_DATE: applied.checkedDate,
                  APPLIED_SUCCESS_COUNT: applied.successCount,
                  APPLIED_ERROR_COUNT: applied.errorCount,
                });
              }
            });
          },
        },
        { label: t("btn.cancel"), onClick: () => null },
      ],
    });
  }

  handleReject(batch) {
    this.setState({ inRejectMode: true, rejectBatch: batch, rejectNotes: "" });
  }

  doReject() {
    const { t } = this.props;

    if (!this.state.rejectNotes.trim()) {
      toastError(t("bulkBills.rejectReasonRequired"));
      return;
    }

    post(
      "/api/data/RejectBulkBillBatch",
      { batchId: this.state.rejectBatch.ID, notes: this.state.rejectNotes },
      this.props.updateIsLoading
    ).then((apiResponse) => {
      if (apiResponse && apiResponse.data.respCode === 0) {
        this.setState({ inRejectMode: false, rejectBatch: null, rejectNotes: "" });
        this.getData();
      }
    });
  }

  statusBadge(status) {
    const colors = {
      Pending: { bg: "#fff3cd", fg: "#856404" },
      Approved: { bg: "#d4edda", fg: "#155724" },
      Rejected: { bg: "#f8d7da", fg: "#721c24" },
    };
    const c = colors[status] || { bg: "#e2e3e5", fg: "#383d41" };
    return (
      <span
        style={{
          background: c.bg,
          color: c.fg,
          padding: "2px 10px",
          borderRadius: "20px",
          fontWeight: "600",
          fontSize: "12px",
        }}
      >
        {status}
      </span>
    );
  }

  getColumns() {
    const { t } = this.props;
    return [
      { id: 1, label: t("col.id"), field: "ID", width: "70px" },
      { id: 2, label: t("col.serviceName"), field: "SERVICE_NAME" },
      { id: 3, label: t("bulkBills.fileName"), field: "FILE_NAME" },
      { id: 4, label: t("bulkBills.rows"), field: "ROWS_TOTAL", width: "80px" },
      {
        id: 5,
        label: t("col.amount"),
        field: "TOTAL_AMOUNT",
        cellRenderer: ({ data }) => (data.TOTAL_AMOUNT || 0).toLocaleString(),
      },
      {
        id: 6,
        label: t("col.status"),
        field: "STATUS",
        width: "110px",
        cellRenderer: ({ data }) => this.statusBadge(data.STATUS),
      },
      { id: 7, label: t("bulkBills.createdBy"), field: "CREATED_BY_NAME" },
      { id: 8, label: t("bulkBills.createdDate"), field: "CREATED_DATE" },
      { id: 9, label: t("bulkBills.checkedBy"), field: "CHECKED_BY_NAME" },
      { id: 10, label: t("bulkBills.checkedDate"), field: "CHECKED_DATE" },
      {
        id: 11,
        label: t("bulkBills.result"),
        field: "APPLIED_SUCCESS_COUNT",
        cellRenderer: ({ data }) =>
          data.STATUS === "Approved" ? (
            <span>
              <b style={{ color: "#155724" }}>{data.APPLIED_SUCCESS_COUNT || 0}</b>
              {" / "}
              <b style={{ color: "#721c24" }}>{data.APPLIED_ERROR_COUNT || 0}</b>
            </span>
          ) : (
            ""
          ),
      },
      { id: 12, label: t("bulkBills.notes"), field: "CHECKER_NOTES" },
      {
        id: 13,
        label: t("col.actions"),
        field: "SYS_ACTIONS",
        width: "130px",
        maxResizeWidth: "130px",
        resizable: false,
        pinned: true,
        cellRenderer: ({ data }) => (
          <div id="outer">
            <div className="inner" title={t("bulkBills.viewRows")}>
              <button
                className="dropdown-item btn-popup-menu"
                disabled={this.props.isLoading}
                onClick={() => this.openRows(data)}
              >
                <i className="fa fa-list" />
              </button>
            </div>
            {sysJwt.isAuthorized(["CB97"]) && data.STATUS === "Pending" && (
              <div className="inner" title={t("bulkBills.approve")}>
                <button
                  className="dropdown-item btn-popup-menu"
                  disabled={this.props.isLoading}
                  onClick={() => this.handleApprove(data)}
                >
                  <i className="fa fa-check" />
                </button>
              </div>
            )}
            {sysJwt.isAuthorized(["CB97"]) && data.STATUS === "Pending" && (
              <div className="inner" title={t("bulkBills.reject")}>
                <button
                  className="dropdown-item btn-popup-menu"
                  disabled={this.props.isLoading}
                  onClick={() => this.handleReject(data)}
                >
                  <i className="fa fa-ban" />
                </button>
              </div>
            )}
          </div>
        ),
      },
    ];
  }

  render() {
    const { t } = this.props;
    const { uploadStage } = this.state;

    return (
      <div className="bms-content-component fade-in">
        <div className="row align-items-center">
          <div className="col">
            <h4>{t("pages.bulkBills")}</h4>
          </div>
          <div className="col-auto page-actions">
            {sysJwt.isAuthorized(["CB96"]) && (
              <Button
                label={t("bulkBills.newUpload")}
                icon="fa fa-upload"
                className="p-button-rounded p-button-secondary"
                disabled={this.props.isLoading}
                onClick={() => this.openUpload()}
              />
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="page-filter-bar">
          <span className="filter-label">{t("col.status")}:</span>
          <Dropdown
            value={this.state.filterStatus}
            options={this.state.statusOptions}
            showClear
            onChange={(e) => this.setState({ filterStatus: e.value })}
            placeholder={t("filter.all")}
          />

          <span className="filter-label">{t("filter.service")}:</span>
          <Dropdown
            value={this.state.filterService}
            options={this.state.allServices}
            showClear
            filter
            onChange={(e) => this.setState({ filterService: e.value })}
            placeholder={t("filter.all")}
          />

          <Button
            label={t("btn.search")}
            icon="fa fa-search"
            className="p-button-rounded p-button-secondary"
            disabled={this.props.isLoading}
            onClick={() => this.getData()}
          />
        </div>

        {/* ── Upload dialog (maker) ── */}
        <Dialog
          visible={this.state.inUploadMode}
          style={{ width: uploadStage === "setup" ? "55vw" : "80vw" }}
          onHide={() => this.closeUpload()}
          dismissableMask={false}
          closable={false}
          header={<h5 className="modal-header">{t("bulkBills.newUpload")}</h5>}
          footer={
            <div>
              {uploadStage === "setup" && (
                <Button
                  label={t("bulk.preview")}
                  icon="fa fa-search"
                  className="p-button-rounded p-button-secondary"
                  disabled={
                    !this.state.uploadFile ||
                    !this.state.uploadServiceId ||
                    this.props.isLoading
                  }
                  onClick={() => this.doPreview()}
                />
              )}
              {uploadStage === "preview" && (
                <>
                  <Button
                    label={t("bulkBills.submitForApproval")}
                    icon="fa fa-paper-plane"
                    className="p-button-rounded p-button-success"
                    disabled={
                      this.state.previewErrorCount > 0 || this.props.isLoading
                    }
                    onClick={() => this.doSubmit()}
                  />
                  <Button
                    label={t("bulk.back")}
                    icon="fa fa-arrow-left"
                    className="p-button-rounded p-button-secondary"
                    style={{ marginLeft: "8px" }}
                    disabled={this.props.isLoading}
                    onClick={() => this.setState({ uploadStage: "setup" })}
                  />
                </>
              )}
              <Button
                label={t("btn.close")}
                icon="fa fa-close"
                className="p-button-rounded p-button-danger"
                style={{ marginLeft: "8px" }}
                onClick={() => this.closeUpload()}
              />
            </div>
          }
        >
          {uploadStage === "setup" && (
            <div>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                  {t("col.serviceName")}
                  <span style={{ color: "#DC3545" }}> *</span>:
                </label>
                <Dropdown
                  value={this.state.uploadServiceId}
                  options={this.state.allServices}
                  filter
                  onChange={(e) => this.setState({ uploadServiceId: e.value })}
                  placeholder={t("filter.service")}
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                  {t("col.compound")}:
                </label>
                <Dropdown
                  value={this.state.uploadCompoundId}
                  options={this.state.compoundsDropdown}
                  showClear
                  filter
                  onChange={(e) => this.setState({ uploadCompoundId: e.value || "" })}
                  placeholder={t("bulk.allCompounds")}
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ marginBottom: "16px" }}>
                <Button
                  label={t("btn.downloadTemplate")}
                  icon="fa fa-download"
                  className="p-button-rounded p-button-info"
                  disabled={this.props.isLoading}
                  onClick={() => this.downloadTemplate()}
                />
                <span style={{ marginLeft: "10px", color: "#666", fontSize: "12px" }}>
                  {t("bulkBills.downloadHint")}
                </span>
              </div>
              <hr />
              <div style={{ marginBottom: "10px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                  {t("btn.excelFile")}:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "block" }}
                  onChange={(e) =>
                    this.setState({ uploadFile: e.target.files[0] || null })
                  }
                />
              </div>
              <p style={{ marginTop: "10px", color: "#666", fontSize: "12px" }}>
                {t("bulkBills.uploadHint")}
              </p>
            </div>
          )}

          {uploadStage === "preview" && (
            <div>
              <div style={{ marginBottom: "12px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#d4edda", color: "#155724", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                  <b>{this.state.previewRows.filter((r) => r.status === "Valid").length}</b>
                  {t("bulkBills.toIssue")}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f8d7da", color: "#721c24", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                  <b>{this.state.previewErrorCount}</b>
                  {t("bulk.errors")}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#e2e3e5", color: "#383d41", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                  {t("bulkElec.totalAmount")}:{" "}
                  <b>{(this.state.previewTotalAmount || 0).toLocaleString()}</b>
                </span>
              </div>

              {this.state.previewErrorCount > 0 && (
                <div style={{ marginBottom: "10px", padding: "8px 12px", background: "#f8d7da", color: "#721c24", borderRadius: "4px", fontSize: "13px" }}>
                  {t("bulkBills.errorsBlockSubmit")}
                </div>
              )}

              <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                <table className="table table-sm table-bordered" style={{ fontSize: "13px" }}>
                  <thead className="thead-light" style={{ position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ width: "55px" }}>{t("btn.row")}</th>
                      <th>{t("col.unitName")}</th>
                      <th>{t("col.compound")}</th>
                      <th>{t("col.ownerName")}</th>
                      <th style={{ width: "110px" }}>{t("col.amount")}</th>
                      <th style={{ width: "110px" }}>{t("col.dueDate")}</th>
                      <th>{t("col.notes")}</th>
                      <th style={{ width: "80px" }}>{t("col.status")}</th>
                      <th>{t("col.details")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.previewRows.map((r, i) => (
                      <tr key={i} className={r.status === "Error" ? "table-danger" : "table-success"}>
                        <td>{r.row}</td>
                        <td>{r.unitName}</td>
                        <td>{r.compoundName}</td>
                        <td>{r.ownerName}</td>
                        <td>{(r.amount || 0).toLocaleString()}</td>
                        <td>{r.dueDate}</td>
                        <td>{r.notes}</td>
                        <td>{r.status}</td>
                        <td>{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Dialog>

        {/* ── Batch rows dialog (viewer / checker) ── */}
        <Dialog
          visible={this.state.inRowsMode}
          style={{ width: "80vw" }}
          onHide={() => this.closeRows()}
          dismissableMask={true}
          closable={false}
          header={
            <h5 className="modal-header">
              {t("bulkBills.batchDetails")}
              {this.state.selectedBatch ? ` #${this.state.selectedBatch.ID}` : ""}
            </h5>
          }
          footer={
            <div>
              {sysJwt.isAuthorized(["CB97"]) &&
                this.state.selectedBatch &&
                this.state.selectedBatch.STATUS === "Pending" && (
                  <>
                    <Button
                      label={t("bulkBills.approve")}
                      icon="fa fa-check"
                      className="p-button-rounded p-button-success"
                      disabled={this.props.isLoading}
                      onClick={() => {
                        const batch = this.state.selectedBatch;
                        this.setState({ inRowsMode: false });
                        this.handleApprove(batch);
                      }}
                    />
                    <Button
                      label={t("bulkBills.reject")}
                      icon="fa fa-ban"
                      className="p-button-rounded p-button-warning"
                      style={{ marginLeft: "8px" }}
                      disabled={this.props.isLoading}
                      onClick={() => {
                        const batch = this.state.selectedBatch;
                        this.setState({ inRowsMode: false });
                        this.handleReject(batch);
                      }}
                    />
                  </>
                )}
              <Button
                label={t("btn.close")}
                icon="fa fa-close"
                className="p-button-rounded p-button-danger"
                style={{ marginLeft: "8px" }}
                onClick={() => this.closeRows()}
              />
            </div>
          }
        >
          {this.state.selectedBatch && (
            <div style={{ marginBottom: "12px", display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "13px", color: "black" }}>
              <span>
                <b>{t("col.serviceName")}:</b> {this.state.selectedBatch.SERVICE_NAME}
              </span>
              <span>
                <b>{t("bulkBills.createdBy")}:</b>{" "}
                {this.state.selectedBatch.CREATED_BY_NAME} ({this.state.selectedBatch.CREATED_DATE})
              </span>
              {this.state.selectedBatch.CHECKED_BY_NAME && (
                <span>
                  <b>{t("bulkBills.checkedBy")}:</b>{" "}
                  {this.state.selectedBatch.CHECKED_BY_NAME} ({this.state.selectedBatch.CHECKED_DATE})
                </span>
              )}
              <span>
                <b>{t("col.status")}:</b> {this.statusBadge(this.state.selectedBatch.STATUS)}
              </span>
            </div>
          )}

          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <table className="table table-sm table-bordered" style={{ fontSize: "13px" }}>
              <thead className="thead-light" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ width: "55px" }}>{t("btn.row")}</th>
                  <th>{t("col.unitName")}</th>
                  <th>{t("col.ownerName")}</th>
                  <th style={{ width: "110px" }}>{t("col.amount")}</th>
                  <th style={{ width: "110px" }}>{t("col.dueDate")}</th>
                  <th>{t("col.notes")}</th>
                  <th style={{ width: "110px" }}>{t("col.seqNo")}</th>
                  <th style={{ width: "80px" }}>{t("col.status")}</th>
                  <th>{t("col.details")}</th>
                </tr>
              </thead>
              <tbody>
                {this.state.batchRows.map((r, i) => (
                  <tr
                    key={i}
                    className={
                      r.RESULT_STATUS === "Success"
                        ? "table-success"
                        : r.RESULT_STATUS === "Error"
                        ? "table-danger"
                        : ""
                    }
                  >
                    <td>{r.ROW_NO}</td>
                    <td>{r.UNIT_NAME}</td>
                    <td>{r.OWNER_NAME}</td>
                    <td>{(r.AMOUNT || 0).toLocaleString()}</td>
                    <td>{r.DUE_DATE}</td>
                    <td>{r.NOTES}</td>
                    <td>{r.SEQ_NO}</td>
                    <td>{r.RESULT_STATUS || "-"}</td>
                    <td>{r.RESULT_MESSAGE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Dialog>

        {/* ── Reject dialog (checker) ── */}
        <Dialog
          visible={this.state.inRejectMode}
          style={{ width: "40vw" }}
          onHide={() => this.setState({ inRejectMode: false })}
          dismissableMask={true}
          closable={false}
          header={<h5 className="modal-header">{t("bulkBills.reject")}</h5>}
          footer={
            <div>
              <Button
                label={t("bulkBills.reject")}
                icon="fa fa-ban"
                className="p-button-rounded p-button-warning"
                disabled={this.props.isLoading}
                onClick={() => this.doReject()}
              />
              <Button
                label={t("btn.cancel")}
                icon="fa fa-close"
                className="p-button-rounded p-button-danger"
                style={{ marginLeft: "8px" }}
                onClick={() => this.setState({ inRejectMode: false })}
              />
            </div>
          }
        >
          <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
            {t("bulkBills.rejectReason")}
            <span style={{ color: "#DC3545" }}> *</span>:
          </label>
          <InputText
            value={this.state.rejectNotes}
            onChange={(e) => this.setState({ rejectNotes: e.target.value })}
            style={{ width: "100%" }}
          />
        </Dialog>

        <SysTable
          columns={this.getColumns()}
          rows={this.state.tableData}
          pageSizes={[10, 15, 20, 25, 30]}
        />
      </div>
    );
  }
}

export default withTranslation()(BulkBills);
